/**
 * BrokerageOwnerResolver — Inc-9.
 *
 * Resolves the brokerage contract's owner (party 2) from the extracted national
 * ID against the company's existing owners:
 *  - auto-matches `owner_id_number` → owners.national_id on first render,
 *  - shows the extracted ID inline + a searchable picker (pre-selected from the
 *    match, still editable — overriding to a different national ID warns),
 *  - when no owner matches, offers a pre-filled "Create owner" confirm sub-form
 *    (the new owner becomes the selection, scoped to the importing company).
 *
 * Owner is REQUIRED for the contract, so the picker is always available as the
 * fallback (manual-entry mode / failed extraction).
 */
import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOwners, useCreateOwner } from "@/hooks/useOwners";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/auth/PhoneInput";
import { isValidSaudiPhone, normalizePhoneForDB, toE164Phone } from "@/lib/phone";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { matchOwnerByNationalId, normaliseId } from "@/lib/brokerageMatch";

export interface OwnerExtract {
  owner_id_number?: string | null;
  owner_name_on_contract?: string | null;
  owner_id_type?: string | null;
  owner_contact_no?: string | null;
  owner_email?: string | null;
  owner_national_address?: string | null;
  owner_representative_name?: string | null;
  owner_representative_id?: string | null;
}

interface Props {
  extracted: OwnerExtract;
  /** Selected owner id, "" when none. */
  value: string;
  onChange: (ownerId: string) => void;
  hasError?: boolean;
}

export function BrokerageOwnerResolver({ extracted, value, onChange, hasError }: Props) {
  const { t } = useLanguage();
  const { data: owners = [], isLoading: ownersLoading } = useOwners();
  const createOwner = useCreateOwner();
  // State (not a ref) so flipping it after the auto-match attempt re-renders and
  // the "owner not in system" prompt can appear.
  const [autoResolved, setAutoResolved] = useState(false);

  const extractedId = normaliseId(extracted.owner_id_number);

  // Auto-match once (by national id) when the user hasn't chosen yet.
  useEffect(() => {
    if (autoResolved) return;
    if (value) {
      setAutoResolved(true);
      return;
    }
    // Gate on loading (not list length) so a company with ZERO owners still
    // marks the attempt done → the "create owner" prompt appears on first import.
    if (ownersLoading || !extractedId) return;
    setAutoResolved(true);
    const match = matchOwnerByNationalId(owners, extracted.owner_id_number);
    if (match) onChange(match.id);
  }, [owners, value, ownersLoading, extractedId, extracted.owner_id_number, onChange, autoResolved]);

  const selected = owners.find((o) => o.id === value) ?? null;
  const mismatch =
    !!selected && !!extractedId && normaliseId(selected.national_id) !== extractedId;
  const notFound = autoResolved && !value && !!extractedId;

  // ── Create-owner sub-form ───────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [ownerType, setOwnerType] = useState<"individual" | "commercial">(
    extracted.owner_representative_name || extracted.owner_representative_id
      ? "commercial"
      : "individual",
  );
  const [form, setForm] = useState({
    full_name: extracted.owner_name_on_contract ?? "",
    national_id: extracted.owner_id_number ?? "",
    phone: toE164Phone(extracted.owner_contact_no),
    email: extracted.owner_email ?? "",
  });

  const handleCreate = async () => {
    if (!form.full_name.trim()) {
      toast.error(t("اسم المالك مطلوب", "Owner name is required"));
      return;
    }
    if (form.phone && !isValidSaudiPhone(form.phone)) {
      toast.error(t("يجب أن يبدأ رقم الجوال بـ 5 ويتكون من 9 أرقام", "Phone must start with 5 and be 9 digits"));
      return;
    }
    try {
      const owner = await createOwner.mutateAsync({
        full_name: form.full_name.trim(),
        national_id: form.national_id.trim() || null,
        phone: normalizePhoneForDB(form.phone),
        email: form.email.trim() || null,
        address: extracted.owner_national_address || null,
        owner_type: ownerType,
        representative_name:
          ownerType === "commercial" ? extracted.owner_representative_name || null : null,
        representative_id_number:
          ownerType === "commercial" ? extracted.owner_representative_id || null : null,
      });
      if (owner?.id) {
        onChange(owner.id);
        setShowCreate(false);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("تعذّر إنشاء المالك", "Failed to create owner"));
    }
  };

  return (
    <div className="space-y-2">
      <Label>
        {t("المالك", "Owner")}
        <span className="text-destructive ms-1">*</span>
      </Label>

      {/* Extracted identity from the contract */}
      {(extractedId || extracted.owner_name_on_contract) && (
        <p className="text-xs text-muted-foreground" data-testid="owner-extracted-id">
          {t("من العقد:", "From contract:")}{" "}
          <span className="font-medium text-foreground">
            {extracted.owner_name_on_contract || "—"}
          </span>
          {extractedId ? ` · ${t("هوية", "ID")} ${extracted.owner_id_number}` : ""}
        </p>
      )}

      <SearchableSelect
        items={owners}
        value={value || null}
        onValueChange={(v) => onChange(v ?? "")}
        getKey={(o) => o.id}
        getLabel={(o) => o.full_name}
        getSubtitle={(o) => o.national_id}
        getSearchString={(o) => `${o.full_name} ${o.national_id ?? ""} ${o.phone ?? ""}`}
        placeholder={t("اختر مالكاً", "Select an owner")}
        searchPlaceholder={t("ابحث بالاسم أو الهوية...", "Search by name or ID...")}
        emptyText={t("لا يوجد مالك مطابق", "No matching owner")}
        hasError={hasError}
      />

      {/* Auto-matched confirmation */}
      {selected && !mismatch && extractedId && normaliseId(selected.national_id) === extractedId && (
        <p className="text-xs text-success">
          {t("تم مطابقة المالك تلقائياً بالهوية", "Owner auto-matched by national ID")}
        </p>
      )}

      {/* Mismatch warning (override to a different national ID) */}
      {mismatch && (
        <p className="flex items-start gap-1 text-xs text-amber-600 dark:text-amber-500">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          {t(
            `هوية المالك المختار لا تطابق الهوية المستخرجة (${extracted.owner_id_number})`,
            `Selected owner's national ID doesn't match the extracted ID (${extracted.owner_id_number})`,
          )}
        </p>
      )}

      {/* Not in the system → create */}
      {!showCreate && notFound && !selected && (
        <div className="rounded-lg border border-dashed p-3 text-sm space-y-2">
          <p className="text-muted-foreground">
            {t(
              "هذا المالك غير موجود في النظام.",
              "This owner isn't in the system.",
            )}
          </p>
          <Button type="button" size="sm" variant="outline" onClick={() => setShowCreate(true)}>
            <UserPlus className="h-4 w-4 me-2" />
            {t("إنشاء المالك من العقد", "Create owner from contract")}
          </Button>
        </div>
      )}

      {/* Pre-filled create sub-form */}
      {showCreate && (
        <div className="rounded-lg border p-3 space-y-3 bg-muted/30">
          <p className="text-sm font-medium">{t("إنشاء مالك جديد", "Create new owner")}</p>
          <div className="flex gap-2 text-xs">
            {(["individual", "commercial"] as const).map((tType) => (
              <Button
                key={tType}
                type="button"
                size="sm"
                variant={ownerType === tType ? "default" : "outline"}
                onClick={() => setOwnerType(tType)}
              >
                {tType === "individual" ? t("فرد", "Individual") : t("منشأة", "Commercial")}
              </Button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{t("الاسم", "Name")} *</Label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                data-field="new_owner_full_name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("رقم الهوية", "National ID")}</Label>
              <Input
                value={form.national_id}
                onChange={(e) => setForm((f) => ({ ...f, national_id: e.target.value }))}
                data-field="new_owner_national_id"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("الجوال", "Phone")}</Label>
              <PhoneInput
                value={form.phone}
                onChange={(phone) => setForm((f) => ({ ...f, phone }))}
                data-testid="new_owner_phone"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("البريد الإلكتروني", "Email")}</Label>
              <Input
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-field="new_owner_email"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setShowCreate(false)}>
              {t("إلغاء", "Cancel")}
            </Button>
            <Button type="button" size="sm" onClick={handleCreate} disabled={createOwner.isPending}>
              {createOwner.isPending && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              {t("إنشاء المالك", "Create owner")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
